/* MySchoolRx — Terms of Use. Operated by 589 Labs LLC.
   Contains only verifiable/general provisions; no invented billing, deadlines,
   pricing, service guarantees, or internal verification notes. */
import React from "react";
import { PublicPageShell, BLUE } from "./publicChrome";

const EFFECTIVE_DATE = "June 2026";

const Wrap = ({ children }) => (
  <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">{children}</section>
);
const H2 = ({ children }) => (
  <h2 className="mt-10 text-[24px] sm:text-[26px] font-bold text-[#111827] tracking-tight">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-4 text-[17px] leading-relaxed text-[#4b5563]">{children}</p>
);

const MySchoolRxTerms = () => (
  <PublicPageShell
    title="MySchoolRx Terms of Use | 589 Labs LLC"
    description="Terms of Use for the MySchoolRx website and MySchoolRx platform, operated by 589 Labs LLC."
    canonicalPath="/terms"
    testid="terms-page"
  >
    <div className="border-b border-[#e5e7eb] bg-[#f9fbfc]">
      <Wrap>
        <div className="py-12 sm:py-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] tracking-tight leading-[1.08]" data-testid="terms-title">
            MySchoolRx Terms of Use
          </h1>
          <p className="mt-4 text-[16px] text-[#6b7280]" data-testid="terms-effective-date">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </Wrap>
    </div>

    <Wrap>
      <div className="py-10 sm:py-14 pb-16">
        <H2>About these terms</H2>
        <P>
          These terms govern use of the MySchoolRx website and platform operated by 589 Labs LLC. Any separately
          executed agreement with your organization may contain additional terms governing its use of the service.
        </P>

        <H2>Purpose of the service</H2>
        <P>
          MySchoolRx provides a structured MySchoolRx and related reporting tools to support
          organizational assessment and discussion.
        </P>
        <P>
          Results depend on submitted information and the applicable assessment model. They do not constitute legal
          advice, an audit opinion, accreditation, authorizer approval, or a guarantee of school performance.
        </P>

        <H2>Accounts and authorized use</H2>
        <P>
          Use only accounts and organizational access assigned to you. Keep your credentials confidential and notify us
          if you suspect unauthorized access.
        </P>
        <P>
          Do not access another organization&apos;s information without authorization, attempt to bypass permissions,
          disrupt the service, or submit unlawful content.
        </P>

        <H2>Information you submit</H2>
        <P>
          Submit information you are authorized to provide. Take reasonable care that your responses are accurate and
          that supporting material is appropriate for the assessment.
        </P>
        <P>
          Avoid submitting student-identifiable or other sensitive personal information unless expressly authorized under
          applicable arrangements. Your organization remains responsible for the information it submits and for its
          permission to provide that information.
        </P>

        <H2>Assessments and reports</H2>
        <P>
          Diagnostic results support professional judgment and should be reviewed in context. Your organization remains
          responsible for its decisions, reporting obligations, and actions.
        </P>
        <P>
          Where school and Portfolio assessments are available, they represent distinct perspectives and should be
          interpreted accordingly.
        </P>

        <H2>Founding Partner access</H2>
        <P>
          The Founding Partner program is free for a limited time. No payment is required to join.
        </P>
        <P>
          Participation does not guarantee permanent free access or delivery of a particular future feature.
        </P>

        <H2>Privacy</H2>
        <P>
          Information handling is described in the{" "}
          <a href="/privacy" className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="terms-privacy-link">
            MySchoolRx Privacy Notice
          </a>{" "}
          and any applicable organizational agreement.
        </P>

        <H2>Service changes and support</H2>
        <P>
          We may update, maintain, or adjust the service over time. Availability, maintenance, support, suspension, and
          account closure are handled in accordance with our standard practices and any applicable organizational
          agreement.
        </P>

        <H2>Legal provisions</H2>
        <P>
          Intellectual property, liability, disputes, governing law, and termination are addressed in our standard
          provisions and any applicable organizational agreement. Nothing on this page creates rights beyond those set
          out in an executed agreement with 589 Labs LLC.
        </P>

        <H2>Contact</H2>
        <P>
          Questions about these terms can be submitted to 589 Labs LLC through{" "}
          <a href="/contact" className="font-semibold hover:underline" style={{ color: BLUE }} data-testid="terms-contact-link">
            Contact MySchoolRx
          </a>.
        </P>

        <p className="mt-10 text-[15px] text-[#6b7280]" data-testid="terms-operator-line">
          MySchoolRx is operated by 589 Labs LLC.
        </p>
      </div>
    </Wrap>
  </PublicPageShell>
);

export default MySchoolRxTerms;
